import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import type { BreadcrumbItem } from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const categoryHeroImages: Record<string, string> = {
  'tianmei-lolita': 'https://readdy.ai/api/search-image?query=Sweet%20Lolita%20fashion%20with%20pink%20dresses%20lace%20ribbons%20and%20bows%2C%20dreamy%20pastel%20candy%20shop%20background%2C%20soft%20pink%20and%20cream%20tones%2C%20kawaii%20feminine%20editorial%20photography%20with%20gentle%20lighting&width=1800&height=500&seq=cat-hero-tianmei&orientation=landscape',
  'gudian-lolita': 'https://readdy.ai/api/search-image?query=Classic%20Lolita%20fashion%20with%20Victorian%20elegant%20dresses%20in%20burgundy%20and%20navy%2C%20antique%20library%20background%20with%20warm%20wood%20tones%2C%20sophisticated%20vintage%20editorial%20photography&width=1800&height=500&seq=cat-hero-gudian&orientation=landscape',
  'gete-lolita': 'https://readdy.ai/api/search-image?query=Gothic%20Lolita%20fashion%20with%20black%20lace%20dresses%20and%20dark%20elegant%20aesthetic%2C%20moonlit%20cathedral%20interior%20background%2C%20dramatic%20romantic%20editorial%20photography%20with%20soft%20shadows&width=1800&height=500&seq=cat-hero-gete&orientation=landscape',
  'zhonghuafeng-lolita': 'https://readdy.ai/api/search-image?query=Chinese%20style%20Lolita%20fashion%20with%20traditional%20embroidery%20and%20silk%2C%20elegant%20Eastern%20aesthetic%2C%20warm%20lantern-lit%20traditional%20courtyard%20background%2C%20cultural%20fusion%20editorial%20photography&width=1800&height=500&seq=cat-hero-zhonghua&orientation=landscape',
  'chuanda-zhinan': 'https://readdy.ai/api/search-image?query=Elegant%20Lolita%20fashion%20wardrobe%20styling%20with%20various%20dresses%20and%20accessories%20arranged%20beautifully%2C%20soft%20cream%20vanity%20room%20background%2C%20fashion%20styling%20editorial%20photography%20with%20natural%20light&width=1800&height=500&seq=cat-hero-chuanda&orientation=landscape',
  'pinpai-tuijian': 'https://readdy.ai/api/search-image?query=Luxury%20Lolita%20brand%20boutique%20storefront%20with%20elegant%20dress%20displays%2C%20refined%20shopping%20atmosphere%2C%20warm%20boutique%20interior%20lighting%2C%20fashion%20retail%20editorial%20photography&width=1800&height=500&seq=cat-hero-pinpai&orientation=landscape',
  'huodong-zixun': 'https://readdy.ai/api/search-image?query=Lolita%20fashion%20tea%20party%20event%20with%20beautifully%20dressed%20participants%2C%20elegant%20floral%20venue%20decoration%2C%20warm%20social%20gathering%20atmosphere%2C%20afternoon%20light%20editorial%20photography&width=1800&height=500&seq=cat-hero-huodong&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);
  const [activeCat, setActiveCat] = useState(slug || '');

  useEffect(() => {
    if (slug) setActiveCat(slug);
  }, [slug]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-emotion-sad-line text-2xl text-foreground-500"></i>
            </div>
            <h1 className="text-lg font-heading font-semibold text-foreground-900 mb-2">分类未找到</h1>
            <p className="text-sm text-foreground-600 mb-4">抱歉，您查找的分类不存在或已被移除。</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: '首页', href: '/' },
    { label: currentCategory.name },
  ];

  const heroImage = categoryHeroImages[slug || ''] || categoryHeroImages['chuanda-zhinan'];

  return (
    <>
      <Navbar />

      <main>
        {/* Category Hero */}
        <section className="relative w-full h-[280px] md:h-[340px] overflow-hidden">
          <img
            src={heroImage}
            alt={currentCategory.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/35"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-xl md:text-3xl font-heading font-bold text-background-50 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-background-50/85 max-w-lg leading-relaxed">
              {currentCategory.description}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto px-4 md:px-6 overflow-x-auto">
            <div className="flex items-center gap-1 py-3 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 ${
                    activeCat === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-secondary-100 hover:text-foreground-800'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="mb-6 md:mb-8">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-file-list-3-line text-xl text-foreground-500"></i>
                </div>
                <p className="text-sm text-foreground-600">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}